import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { PRIORITIES, SPEC_ASSET_TYPES, specPropertiesFor } from '../../lib/art-pipeline.js';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * The "+ Spec" form: what a new spec needs (a name and an asset type), plus
 * the type's properties and the optional fields worth setting up front. "Add &
 * add another" keeps the form open, with its type, properties, priority and
 * milestone kept, for capturing several specs in a row.
 *
 * Fires `spec-created` (detail: the new spec) and `close`.
 */
class PipelineSpecForm extends BaseComponent {
    static properties = {
        milestones: { type: Array, attribute: false },
        // Starting values, e.g. the grid's active filters.
        defaults: { type: Object, attribute: false },
        _form: { type: Object, state: true },
        _saving: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _added: { type: Number, state: true },
    };

    static styles = css`
        :host { display: block; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9998; }
        .modal-panel { background: white; border-radius: 8px; width: 90vw; max-width: 520px; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 1rem 3rem rgba(0,0,0,0.2); }
        .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid #dee2e6; }
        .modal-header h5 { margin: 0; font-weight: 600; }
        .modal-body { padding: 1rem 1.25rem; overflow-y: auto; font-size: 0.9rem; }
        .modal-footer { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; padding: 0.75rem 1.25rem; border-top: 1px solid #dee2e6; }
        .row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
        .form-label { font-size: 0.8rem; font-weight: 600; margin-bottom: 0.25rem; }
        .optional { font-weight: 400; color: #888; }
    `;

    constructor() {
        super();
        this.milestones = [];
        this.defaults = {};
        this._form = null;
        this._saving = false;
        this._error = null;
        this._added = 0;
    }

    connectedCallback() {
        super.connectedCallback();
        this._form = {
            name: '',
            asset_type: this.defaults.asset_type || 'miniature',
            properties: {},
            priority: this.defaults.priority || 'medium',
            milestone_id: this.defaults.milestone_id || '',
            brief: '',
        };
    }

    firstUpdated() {
        this._focusName();
    }

    _focusName() {
        /** @type {HTMLInputElement} */ (this.shadowRoot.querySelector('#spec-name'))?.focus();
    }

    _close() {
        this.dispatchEvent(new CustomEvent('close'));
    }

    _set(key) {
        return (e) => (this._form = { ...this._form, [key]: e.target.value });
    }

    _setType(e) {
        // A type has its own properties, so a new type starts without any.
        this._form = { ...this._form, asset_type: e.target.value, properties: {} };
    }

    _setProperty(key, value) {
        this._form = { ...this._form, properties: { ...this._form.properties, [key]: value } };
    }

    async _submit(another) {
        if (!this._form.name.trim()) {
            this._error = 'Give the spec a name.';
            this._focusName();
            return;
        }
        this._saving = true;
        this._error = null;
        try {
            const created = await api.post('/api/specs', {
                name: this._form.name.trim(),
                asset_type: this._form.asset_type,
                properties: Object.fromEntries(
                    Object.entries(this._form.properties).filter(([, v]) => v),
                ),
                priority: this._form.priority,
                milestone_id: this._form.milestone_id || null,
                brief: this._form.brief.trim() || null,
            });
            this.dispatchEvent(new CustomEvent('spec-created', { detail: created }));
            if (another) {
                this._added += 1;
                this._form = { ...this._form, name: '', brief: '' };
                await this.updateComplete;
                this._focusName();
            } else {
                this._close();
            }
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    /** The selected type's own properties, if it has any. */
    _renderProperties(f) {
        const props = specPropertiesFor(f.asset_type);
        if (!props.length) return nothing;
        return html`
            <div class="row-2 mb-3">
                ${props.map(
                    (p) => html`<div>
                        <label class="form-label" for="spec-prop-${p.key}">${p.label}${p.neededToCommission ? nothing : html` <span class="optional">(optional)</span>`}</label>
                        <select id="spec-prop-${p.key}" class="form-select" @change="${(e) => this._setProperty(p.key, e.target.value)}">
                            <option value="" ?selected="${!f.properties[p.key]}">${p.neededToCommission ? 'Not decided yet' : 'None'}</option>
                            ${p.options.map((o) => html`<option value="${o.value}" ?selected="${f.properties[p.key] === o.value}">${o.label}</option>`)}
                        </select>
                    </div>`,
                )}
            </div>
        `;
    }

    render() {
        if (!this._form) return nothing;
        const f = this._form;
        return html`
            <div class="modal-overlay" @click="${(e) => e.target === e.currentTarget && this._close()}">
                <div class="modal-panel" role="dialog" aria-label="New spec"
                    @keydown="${(e) => e.key === 'Escape' && this._close()}">
                    <div class="modal-header">
                        <h5>New Spec</h5>
                        <button class="btn-close" aria-label="Close" @click="${this._close}"></button>
                    </div>
                    <form class="modal-body" @submit="${(e) => {
                        e.preventDefault();
                        this._submit(false);
                    }}">
                        ${this._error ? html`<div class="alert alert-danger py-2 small">${this._error}</div>` : nothing}
                        <div class="mb-3">
                            <label class="form-label" for="spec-name">What's the art? *</label>
                            <input id="spec-name" class="form-control" required placeholder="e.g. Goblin shaman, Sunken temple map"
                                .value="${f.name}" @input="${this._set('name')}">
                        </div>
                        <div class="row-2 mb-3">
                            <div>
                                <label class="form-label" for="spec-type">Asset type *</label>
                                <select id="spec-type" class="form-select" @change="${this._setType}">
                                    ${SPEC_ASSET_TYPES.map((t) => html`<option value="${t.value}" ?selected="${f.asset_type === t.value}">${t.label}</option>`)}
                                </select>
                            </div>
                            <div>
                                <label class="form-label" for="spec-priority">Priority</label>
                                <select id="spec-priority" class="form-select" @change="${this._set('priority')}">
                                    ${PRIORITIES.map((p) => html`<option value="${p.value}" ?selected="${f.priority === p.value}">${p.label}</option>`)}
                                </select>
                            </div>
                        </div>
                        ${this._renderProperties(f)}
                        <div class="mb-3">
                            <label class="form-label" for="spec-milestone">Milestone <span class="optional">(optional)</span></label>
                            <select id="spec-milestone" class="form-select" @change="${this._set('milestone_id')}">
                                <option value="" ?selected="${!f.milestone_id}">None</option>
                                ${this.milestones.map((m) => html`<option value="${m.id}" ?selected="${f.milestone_id === m.id}">${m.name}</option>`)}
                            </select>
                        </div>
                        <div>
                            <label class="form-label" for="spec-brief">Brief <span class="optional">(optional)</span></label>
                            <textarea id="spec-brief" class="form-control" rows="3" placeholder="What it's for, style, pose, where it's used…"
                                .value="${f.brief}" @input="${this._set('brief')}"></textarea>
                        </div>
                        <button type="submit" hidden></button>
                    </form>
                    <div class="modal-footer">
                        <span class="small text-muted">${this._added ? `${this._added} added` : 'Reference images can be added after.'}</span>
                        <div class="d-flex gap-2">
                            <button class="btn btn-outline-secondary" ?disabled="${this._saving}" @click="${() => this._submit(true)}">Add &amp; add another</button>
                            <button class="btn btn-primary" ?disabled="${this._saving}" @click="${() => this._submit(false)}">Add spec</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('pipeline-spec-form', PipelineSpecForm);
